"use client";

import { useState, useEffect, useRef, useCallback, ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { Search, ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { ChatHistory as ChatHistoryType } from "@/types";
import {
   Input,
   Dialog,
   Skeleton,
   ScrollArea,
   DialogTitle,
   DialogHeader,
   DialogContent
} from "@/app/components/ui";
import { useAuth } from "@/app/hooks";
import { KEY, PROJECTS } from "@/app/lib/enums";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";

interface Props {
   shareKey?: string;
}

const DEBOUNCE_TIME = 300;
const DEFAULT_LIMIT = 25;
const MAX_PREVIEW_LENGTH = 300;
const MIN_CONTEXT_SIZE = 30;
const MIN_CONTEXT_SIZE_PHRASE = 50;
const CONTEXT_MULTIPLIER = 2;
const SKELETON_ITEMS_COUNT = 6;

export function ChatSearchDialog({ shareKey }: Props) {
   const router = useRouter();
   const translate = useTranslations("Home");
   const { user } = useAuth();
   const [searchQuery, setSearchQuery] = useState("");
   const [searchResults, setSearchResults] = useState<ChatHistoryType[]>([]);
   const [isSearching, setIsSearching] = useState(false);
   const [selectedIndex, setSelectedIndex] = useState(0);
   const inputRef = useRef<HTMLInputElement>(null);
   const { chatHistories, setHistoryItem, isSearchDialogOpen, setSearchDialogOpen } =
      useStateHistoryChatPage();
   const { selectedBaseKnowledge } = useAiSourceSelector();

   useEffect(() => {
      if (!isSearchDialogOpen) {
         setSearchQuery("");
         setSearchResults([]);
         setSelectedIndex(0);
      }
      if (isSearchDialogOpen) {
         if (inputRef.current) {
            setTimeout(() => {
               inputRef.current?.focus();
            }, 100);
         }
         setIsSearching(true);
         try {
            setSearchResults(chatHistories);
            setSelectedIndex(chatHistories.length > 0 ? 0 : -1);
         } finally {
            setIsSearching(false);
         }
      }
   }, [isSearchDialogOpen, chatHistories]);

   const performSearch = useCallback(async () => {
      setIsSearching(true);
      try {
         const value = searchQuery.trim();
         if (!value || !user?.id) {
            setSearchResults([]);
            return;
         }
         const knowledgeBase = [selectedBaseKnowledge.id as PROJECTS];
         const url =
            api_url.chat_history_search_url +
            `?userId=${user.id}&knowledgeBase=${JSON.stringify(
               knowledgeBase
            )}&query=${encodeURIComponent(value)}&limit=${DEFAULT_LIMIT}`;
         const response = await fetchWithAuth(url);
         if (!response.ok) {
            throw new Error("Failed to search chat histories");
         }

         const result = await response.json();
         setSearchResults(result.data?.items || []);
         setSelectedIndex(result.data?.items?.length > 0 ? 0 : -1);
      } catch (error) {
         console.error("Error searching chat history:", error);
         const query = searchQuery.toLowerCase();
         const results = chatHistories.filter(
            (item) =>
               item.title.toLowerCase().includes(query) ||
               (item.messages?.items?.[0]?.content &&
                  item.messages.items[0].content.toLowerCase().includes(query))
         );
         setSearchResults(results);
         setSelectedIndex(results.length > 0 ? 0 : -1);
      } finally {
         setIsSearching(false);
      }
   }, [searchQuery, user?.id, chatHistories, selectedBaseKnowledge.id]);

   useEffect(() => {
      const delaySearch = setTimeout(() => {
         performSearch();
      }, DEBOUNCE_TIME);
      return () => clearTimeout(delaySearch);
   }, [searchQuery, performSearch]);

   const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
         if (e.key === KEY.Escape) {
            setSearchDialogOpen(false);
         } else if (e.key === KEY.ArrowDown) {
            e.preventDefault();
            setSelectedIndex((prev) =>
               prev < searchResults.length - 1 ? prev + 1 : prev
            );
         } else if (e.key === KEY.ArrowUp) {
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
         } else if (
            e.key === KEY.Enter &&
            selectedIndex >= 0 &&
            searchResults.length > 0
         ) {
            handleSelect(searchResults[selectedIndex]);
         }
      },
      [searchResults, selectedIndex, setSearchDialogOpen]
   );

   const handleSelect = useCallback(
      (item: ChatHistoryType) => {
         setHistoryItem(item);
         if (shareKey) {
            router.push("/");
         } else {
            router.push(`/c/${item.id}`);
         }
         setSearchDialogOpen(false);
      },
      [setHistoryItem, shareKey, router, setSearchDialogOpen]
   );

   const getTextWithVisibleMatch = useCallback(
      (text: string, query: string): string => {
         if (!query.trim()) return text;

         const queryLower = query.trim().toLowerCase();
         const textLower = text.toLowerCase();
         const matchIndex = textLower.indexOf(queryLower);

         if (matchIndex === -1) {
            return text.length <= MAX_PREVIEW_LENGTH
               ? text
               : text.substring(0, MAX_PREVIEW_LENGTH) + "...";
         }

         if (text.length <= MAX_PREVIEW_LENGTH) return text;

         const queryLength = queryLower.length;
         const minContextSize = Math.max(MIN_CONTEXT_SIZE, queryLength);

         const contextSize = queryLower.includes(" ")
            ? Math.max(MIN_CONTEXT_SIZE_PHRASE, queryLength * CONTEXT_MULTIPLIER)
            : minContextSize;

         if (matchIndex < contextSize) {
            const endPos = Math.min(matchIndex + queryLength + contextSize, text.length);
            return text.substring(0, endPos) + (endPos < text.length ? "..." : "");
         }

         if (matchIndex > text.length - (queryLength + contextSize)) {
            const startPos = Math.max(0, text.length - contextSize * CONTEXT_MULTIPLIER);
            return (startPos > 0 ? "..." : "") + text.substring(startPos);
         }

         const start = Math.max(0, matchIndex - contextSize);
         const end = Math.min(text.length, matchIndex + queryLength + contextSize);

         return (
            (start > 0 ? "..." : "") +
            text.substring(start, end) +
            (end < text.length ? "..." : "")
         );
      },
      [searchQuery]
   );

   const highlightMatch = useCallback(
      (text: string, query: string): ReactNode => {
         if (!query.trim()) return text;

         const queryLower = query.trim().toLowerCase();
         const textLower = text.toLowerCase();
         const parts: { text: string; isMatch: boolean }[] = [];

         let lastIndex = 0;
         let matchIndex = textLower.indexOf(queryLower);

         while (matchIndex !== -1) {
            if (matchIndex > lastIndex) {
               parts.push({
                  text: text.substring(lastIndex, matchIndex),
                  isMatch: false
               });
            }

            parts.push({
               text: text.substring(matchIndex, matchIndex + queryLower.length),
               isMatch: true
            });

            lastIndex = matchIndex + queryLower.length;
            matchIndex = textLower.indexOf(queryLower, lastIndex);
         }
         if (lastIndex < text.length) {
            parts.push({
               text: text.substring(lastIndex),
               isMatch: false
            });
         }

         return (
            <>
               {parts.map((part, i) =>
                  part.isMatch ? (
                     <span
                        key={i}
                        className="bg-yellow-200 dark:bg-yellow-800 font-medium rounded"
                     >
                        {part.text}
                     </span>
                  ) : (
                     part.text
                  )
               )}
            </>
         );
      },
      [searchQuery]
   );

   const renderSkeletonItems = useCallback(() => {
      return Array(SKELETON_ITEMS_COUNT)
         .fill(0)
         .map((_, index) => (
            <div
               key={"skeleton-" + index}
               className="p-3 rounded-lg"
               style={{ minHeight: "64px" }}
            >
               <Skeleton className="h-5 w-[80%] mb-2" />
               <Skeleton className="h-3 w-[60%] mb-2" />
               <Skeleton className="h-3 w-[30%]" />
            </div>
         ));
   }, []);
   return (
      <Dialog open={isSearchDialogOpen} onOpenChange={setSearchDialogOpen}>
         <DialogContent className="sm:max-w-[700px] h-[600px] flex flex-col">
            <DialogHeader>
               <DialogTitle />
            </DialogHeader>

            <div className="relative">
               <Search
                  size={25}
                  className="absolute left-0 top-2 text-muted-foreground"
               />
               <Input
                  ref={inputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={translate("searchChatHistory") + " (Cmd+K / Ctrl+K)"}
                  className="pl-8 pr-4 border-x-0 border-t-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none rounded-none"
                  autoComplete="on"
                  size="lg"
               />
            </div>

            <ScrollArea className="flex-grow overflow-y-auto mt-2 h-[450px]">
               {isSearching ? (
                  renderSkeletonItems()
               ) : searchResults.length > 0 ? (
                  <div className="space-y-1">
                     {searchResults.map((item, index) => (
                        <div
                           key={item.id}
                           onClick={() => handleSelect(item)}
                           className={`p-3 rounded-lg cursor-pointer hover:bg-accent transition-colors ${
                              index === selectedIndex ? "bg-accent" : ""
                           }`}
                        >
                           <div className="font-medium">
                              {highlightMatch(
                                 getTextWithVisibleMatch(item.title, searchQuery),
                                 searchQuery
                              )}
                           </div>
                           {item.messages?.items?.[0]?.content && (
                              <div className="text-sm text-muted-foreground">
                                 {highlightMatch(
                                    getTextWithVisibleMatch(
                                       item.messages.items[0].content,
                                       searchQuery
                                    ),
                                    searchQuery
                                 )}
                              </div>
                           )}
                           <div className="text-xs text-muted-foreground mt-1">
                              {new Date(item.createdAt).toLocaleString()}
                           </div>
                        </div>
                     ))}
                  </div>
               ) : (
                  <div
                     className="py-4 text-center text-muted-foreground rounded-lg bg-muted/30"
                     style={{
                        minHeight: "64px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                     }}
                  >
                     {searchQuery ? "No results found" : "No chat history found"}
                  </div>
               )}
            </ScrollArea>

            {searchResults.length > 0 && (
               <div className="text-xs text-muted-foreground pt-2 border-t mt-2 flex items-center justify-between">
                  <div>{searchResults.length} results</div>
                  <div className="flex space-x-2">
                     <span>
                        <ArrowUpIcon className="h-3 w-3 inline mr-1" /> /{" "}
                        <ArrowDownIcon className="h-3 w-3 inline mr-1" /> to navigate
                     </span>
                     <span>Enter to select</span>
                  </div>
               </div>
            )}
         </DialogContent>
      </Dialog>
   );
}
